import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AuthorizationService } from '../authorization/authorization.service';
import { AmortResponse } from '../../shared/models/amortResponse';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';

@Component({
  selector: 'div.dtf-adminamortizationpage',
  imports: [CommonModule, FormsModule, MatTableModule, MatPaginatorModule, MatSortModule],
  templateUrl: './adminamortization.component.html'
})
export class AdminAmortizationComponent implements OnInit, AfterViewInit {
  accountNumber = '';
  submittedValue = '';
  selectedAccount = '';
  loading = false;
  errorMessage = '';

  displayedColumns: string[] = [
    'paymentReceiptDate',
    'paymentType',
    'totalPayment',
    'principalAmount',
    'interestAmount',
    'otherFee',
    'outStandingBalance'
  ];
  dataSource = new MatTableDataSource<AmortResponse>([]);

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(private auth: AuthorizationService, private cdr: ChangeDetectorRef) {}

  get canSearch(): boolean {
    const len = this.accountNumber.trim().length;
    return len === 10 || len === 13;
  }

  private buildAmortizationRequest(rawValue: string): { contractNumber: string; scheduleCandidates: string[]; selectedAccount: string } | null {
    const trimmed = rawValue.trim();

    // 13-digit value: first 10 contract number + last 3 schedule number.
    if (trimmed.length === 13) {
      const contractNumber = trimmed.substring(0, 10);
      const rawSchedule = trimmed.substring(10, 13);
      const scheduleCandidates = [rawSchedule, '001']
        .filter((value, index, self) => !!value && self.indexOf(value) === index);
      return {
        contractNumber,
        scheduleCandidates,
        selectedAccount: contractNumber + rawSchedule
      };
    }

    // 10-digit value: treat as customer/contract number with default schedule 001.
    if (trimmed.length === 10) {
      return {
        contractNumber: trimmed,
        scheduleCandidates: ['001'],
        selectedAccount: trimmed + '001'
      };
    }

    return null;
  }

  ngOnInit() {
    const prefillAccount = sessionStorage.getItem('adminAmortizationLookup');
    if (prefillAccount && prefillAccount.trim() !== '') {
      this.accountNumber = prefillAccount.trim();
      this.applyFilterInput();
      sessionStorage.removeItem('adminAmortizationLookup');
    }
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  applyFilterInput() {
    this.submittedValue = this.accountNumber.trim();
    this.errorMessage = '';
    if (!this.submittedValue) {
      return;
    }

    const amortLookup = this.buildAmortizationRequest(this.submittedValue);
    if (!amortLookup) {
      this.dataSource.data = [];
      this.selectedAccount = '';
      this.errorMessage = 'Enter a valid 10-digit customer number or 13-digit account number.';
      return;
    }

    this.loading = true;
    this.dataSource.data = [];

    this.selectedAccount = amortLookup.selectedAccount;

    const tryFetchBySchedule = (scheduleIndex: number) => {
      if (scheduleIndex >= amortLookup.scheduleCandidates.length) {
        this.loading = false;
        this.errorMessage = 'No amortization records found for this account number.';
        this.cdr.detectChanges();
        return;
      }

      const currentSchedule = amortLookup.scheduleCandidates[scheduleIndex];
      const amortRequest = {
        contractNumber: amortLookup.contractNumber,
        scheduleNumber: currentSchedule
      };

      this.auth.getAdminAmort(amortRequest).subscribe({
        next: (res: AmortResponse[]) => {
          const data = Array.isArray(res) ? res : [];
          if (data.length > 0) {
            this.dataSource.data = data;
            const paddedSchedule = currentSchedule.padStart(3, '0');
            this.selectedAccount = amortLookup.contractNumber + paddedSchedule;
            if (this.paginator) {
              this.dataSource.paginator = this.paginator;
              this.paginator.firstPage();
            }
            if (this.sort) {
              this.dataSource.sort = this.sort;
            }
            this.loading = false;
            this.cdr.detectChanges();
            return;
          }
          this.cdr.detectChanges();
          tryFetchBySchedule(scheduleIndex + 1);
        },
        error: () => {
          tryFetchBySchedule(scheduleIndex + 1);
          this.cdr.detectChanges();
        }
      });
    };

    tryFetchBySchedule(0);
    this.cdr.detectChanges();
    sessionStorage.setItem('adminAmortizationLookup', this.submittedValue);
  }

  numberOnly(event: KeyboardEvent) {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
    }
  }
}
